/**
 * Eventi analytics dell'onboarding v3.
 *
 * Backend: window.dataLayer push (GTM-compatible). Niente nuova dipendenza npm.
 * Il giorno che entra Mixpanel/PostHog, swap del backend in un solo file.
 *
 * Convenzione naming: `flusia.<domain>.<event>` (es. flusia.onboarding.wizard_opened).
 */

export type OnboardingEventName =
  | 'flusia.onboarding.wizard_opened'
  | 'flusia.onboarding.step_viewed'
  | 'flusia.onboarding.step_completed'
  | 'flusia.onboarding.step_skipped'
  | 'flusia.onboarding.wizard_dismissed'
  | 'flusia.onboarding.wizard_completed'
  | 'flusia.onboarding.flow_started'
  | 'flusia.onboarding.checklist_task_completed'
  | 'flusia.onboarding.checklist_reconciled'
  | 'flusia.onboarding.discover_unlocked'
  | 'flusia.onboarding.discover_clicked'
  | 'flusia.onboarding.setup_call_click'
  | 'flusia.onboarding.intake_skipped'
  | 'flusia.onboarding.intake_llm_started'
  | 'flusia.onboarding.intake_llm_success'
  | 'flusia.onboarding.intake_llm_timeout'
  | 'flusia.onboarding.intake_llm_unavailable'
  | 'flusia.onboarding.intake_llm_failed'
  | 'flusia.onboarding.chat_opened'
  | 'flusia.onboarding.chat_message_sent'
  | 'flusia.onboarding.chat_response'
  | 'flusia.onboarding.chat_error'
  | 'flusia.trial.banner_viewed'
  | 'flusia.trial.cta_clicked'
  | 'flusia.trial.splash_shown'
  | 'flusia.feature_lock.modal_opened'
  | 'flusia.feature_lock.cta_clicked'
  | 'flusia.discovery.section_hint_viewed'
  | 'flusia.discovery.section_hint_cta_clicked'
  | 'flusia.discovery.section_hint_dismissed'
  | 'flusia.referral.code_shared'
  | 'flusia.referral.code_applied'
  | 'flusia.register.step_viewed'
  | 'flusia.register.started';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/** Step canonici del funnel di registrazione (ordine reale del flusso). */
export type RegisterStep =
  | 'entry'      // /register — form email
  | 'verify'     // /register/verify — codice email
  | 'password'   // /register/password
  | 'profile'    // /register/profile (persona fisica)
  | 'company'    // /register/company
  | 'complete'   // /register/complete — register-complete edge
  | 'discovery'  // /register/discovery — "come ci hai conosciuto"
  | 'connect'    // /register/connect — Google/Microsoft
  | 'modules'    // /register/modules — aree di interesse
  | 'team';      // /register/team — inviti

/** Posizione dello step nel funnel (1-based), per ordinare i report GA4. */
const REGISTER_STEP_INDEX: Record<RegisterStep, number> = {
  entry: 1, verify: 2, password: 3, profile: 4, company: 5,
  complete: 6, discovery: 7, connect: 8, modules: 9, team: 10,
};

let registrationBegun = false;

/** Vista di uno step di registrazione. Nel dataLayer finiscono DUE eventi:
 *  - `flusia.register.step_viewed` (variabile `register_step`), storico;
 *  - `registration_step` (parametri `step`, `step_index`), nome piatto per il
 *    tag GA4 di GTM. Sul primo step parte anche `begin_registration`, una sola
 *    volta per caricamento di pagina.
 *  Gli eventi verso GA4 passano SOLO da GTM (vedi marketingTags.ts): il
 *  container deve avere un tag GA4 per `registration_step` e uno per
 *  `begin_registration`, altrimenti restano nel dataLayer e basta. */
export function trackRegisterStepViewed(step: RegisterStep): void {
  trackOnboardingEvent('flusia.register.step_viewed', { register_step: step });
  if (step === 'entry' && !registrationBegun) {
    registrationBegun = true;
    trackGa4Event('begin_registration', { method: 'email' });
  }
  trackGa4Event('registration_step', { step, step_index: REGISTER_STEP_INDEX[step] });
}

/** Evento GA4 "piatto" (nome standard, es. `sign_up`, `registration_step`)
 *  pushato nel dataLayer per il container GTM. Crea il dataLayer se manca:
 *  un push su `undefined` perderebbe l'evento in silenzio. */
export function trackGa4Event(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...(params ?? {}) });
  if (import.meta.env.DEV) {
    console.log('[analytics]', event, params);
  }
}

export function trackOnboardingEvent(
  event: OnboardingEventName,
  payload?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...(payload ?? {}) });

  if (import.meta.env.DEV) {
    console.log('[analytics]', event, payload);
  }
}
